import { t } from '../../../shared/config/translations'

type MultiSelectSelection = {
	values: string[]
	selectsAll: boolean
}

let isOutsideDismissBound = false

export function bindMultiSelectOutsideDismiss(): void {
	if (isOutsideDismissBound) return
	isOutsideDismissBound = true

	let frame = 0
	const reposition = () => {
		if (frame) return
		frame = requestAnimationFrame(() => {
			frame = 0
			document
				.querySelectorAll<HTMLElement>('.tt-multi-select')
				.forEach(positionMultiSelect)
		})
	}
	window.addEventListener('resize', reposition)
	document.addEventListener(
		'scroll',
		(event) => {
			// Scrolling the options must not reset their size or scroll position.
			if (
				event.target instanceof Element &&
				event.target.closest('.multi-select-menu')
			)
				return
			reposition()
		},
		true,
	)
	window.visualViewport?.addEventListener('resize', reposition)
	window.visualViewport?.addEventListener('scroll', reposition)

	document.addEventListener('pointerdown', (event) => {
		const target = event.target
		if (!(target instanceof Element)) return

		document
			.querySelectorAll<HTMLElement>('.tt-multi-select')
			.forEach((control) => {
				if (!control.contains(target))
					setMultiSelectOpen(control, false)
			})
	})
}

export function toggleMultiSelect(
	rootEl: ParentNode,
	trigger: HTMLButtonElement,
): void {
	const control = trigger.closest<HTMLElement>('.tt-multi-select')
	if (!control) return
	const isOpen = trigger.getAttribute('aria-expanded') === 'true'
	closeMultiSelects(rootEl, control)
	setMultiSelectOpen(control, !isOpen)
}

export function closeMultiSelects(
	rootEl: ParentNode,
	except?: HTMLElement,
): void {
	rootEl
		.querySelectorAll<HTMLElement>('.tt-multi-select')
		.forEach((control) => {
			if (control !== except) setMultiSelectOpen(control, false)
		})
}

export function handleMultiSelectEscape(
	rootEl: ParentNode,
	event: KeyboardEvent,
): boolean {
	if (event.key !== 'Escape') return false
	const openControl = rootEl.querySelector<HTMLElement>(
		'.tt-multi-select:has(.multi-select-trigger[aria-expanded="true"])',
	)
	if (!openControl) return false

	event.preventDefault()
	event.stopPropagation()
	setMultiSelectOpen(openControl, false)
	openControl
		.querySelector<HTMLButtonElement>('.multi-select-trigger')
		?.focus()
	return true
}

export function getNextMultiSelectSelection(
	control: HTMLElement,
	option: HTMLButtonElement,
): MultiSelectSelection {
	const currentValues = getSelectedValues(control)
	if (control.dataset.selectAll !== 'true') {
		const value = option.dataset.value ?? ''
		const allOptionValues = getAllOptionValues(control)
		const values =
			option.dataset.allOption === 'true'
				? currentValues.filter(
						(value) => !allOptionValues.includes(value),
					)
				: currentValues.includes(value)
					? currentValues.filter((item) => item !== value)
					: [...currentValues, value]
		return {
			values,
			selectsAll: !values.some((value) =>
				allOptionValues.includes(value),
			),
		}
	}
	const allOptionValues = getAllOptionValues(control)
	const value = option.dataset.value ?? ''
	const isAllOption = option.dataset.allOption === 'true'
	const isAllSelected =
		isAllOption && option.getAttribute('aria-selected') === 'true'
	const values = isAllOption
		? isAllSelected
			? []
			: allOptionValues
		: currentValues.includes(value)
			? currentValues.filter((item) => item !== value)
			: [...currentValues, value]
	const selectsAll =
		allOptionValues.length > 0 &&
		allOptionValues.every((item) => values.includes(item))

	return { selectsAll, values }
}

export function syncMultiSelect(
	control: HTMLElement,
	values: string[],
	defaultAllSelected: boolean,
): void {
	const selectAll = control.dataset.selectAll === 'true'
	const optionValues = getOptionValues(control)
	const allOptionValues = getAllOptionValues(control)
	const selected = new Set(
		values.filter((value) => optionValues.includes(value)),
	)
	const allSelected =
		defaultAllSelected ||
		(selectAll &&
			allOptionValues.length > 0 &&
			allOptionValues.every((value) => selected.has(value)))
	const partiallySelected = selectAll && !allSelected && selected.size > 0
	const allLabel = control.dataset.allLabel ?? t('common.all')
	let selectedCount = 0
	let selectedLabel = ''

	control
		.querySelectorAll<HTMLButtonElement>('.multi-select-option')
		.forEach((option) => {
			const value = option.dataset.value ?? ''
			const isAll = option.dataset.allOption === 'true'
			const isIncludedInAll = option.dataset.includedInAll !== 'false'
			const isSelected = isAll
				? allSelected
				: isIncludedInAll
					? (selectAll && allSelected) ||
						(!allSelected && selected.has(value))
					: selected.has(value)
			option.setAttribute(
				selectAll ? 'aria-selected' : 'aria-checked',
				String(isSelected),
			)
			option.dataset.indeterminate = String(isAll && partiallySelected)
			if (!isAll && isSelected) {
				selectedCount += 1
				selectedLabel = option.textContent?.trim() ?? selectedLabel
			}
		})

	const valueEl = control.querySelector<HTMLElement>('.multi-select-value')
	if (!valueEl) return
	const selectedIndependentOptions = Array.from(
		control.querySelectorAll<HTMLButtonElement>(
			'.multi-select-option[data-included-in-all="false"]:is([aria-selected="true"], [aria-checked="true"])',
		),
	)
	positionMultiSelect(control)
	valueEl.textContent = allSelected
		? selectedIndependentOptions.length === 0
			? allLabel
			: selectedIndependentOptions.length === 1
				? selectedIndependentOptions[0].textContent?.trim() || allLabel
				: `${selectedIndependentOptions.length} selected`
		: selectedCount === 1
			? selectedLabel
			: `${selectedCount} selected`
}

export function setMultiSelectOpen(
	control: HTMLElement,
	isOpen: boolean,
): void {
	const trigger = control.querySelector<HTMLButtonElement>(
		'.multi-select-trigger',
	)
	const menu = control.querySelector<HTMLElement>('.multi-select-menu')
	trigger?.setAttribute('aria-expanded', String(isOpen))
	if (menu) menu.hidden = !isOpen
	if (isOpen) positionMultiSelect(control)
}

/** Keep every custom select within the viewport and any clipping scroll container. */
function positionMultiSelect(control: HTMLElement): void {
	const menu = control.querySelector<HTMLElement>('.multi-select-menu')
	if (!menu || menu.hidden) return
	const trigger = control.querySelector<HTMLElement>('.multi-select-trigger')
	if (!trigger) return
	const anchor = trigger.getBoundingClientRect()
	const viewport = window.visualViewport
	const inset = 8
	let left = (viewport?.offsetLeft ?? 0) + inset
	let top = (viewport?.offsetTop ?? 0) + inset
	let right =
		left +
		(viewport?.width ?? document.documentElement.clientWidth) -
		2 * inset
	let bottom = top + (viewport?.height ?? window.innerHeight) - 2 * inset
	for (
		let ancestor = control.parentElement;
		ancestor;
		ancestor = ancestor.parentElement
	) {
		// The document scrolls within the viewport; its content box is not a clip.
		if (ancestor === document.body || ancestor === document.documentElement)
			continue
		const style = getComputedStyle(ancestor)
		const rect = ancestor.getBoundingClientRect()
		if (/(auto|scroll|hidden|clip)/.test(style.overflowX)) {
			left = Math.max(left, rect.left + inset)
			right = Math.min(right, rect.right - inset)
		}
		if (/(auto|scroll|hidden|clip)/.test(style.overflowY)) {
			top = Math.max(top, rect.top + inset)
			bottom = Math.min(bottom, rect.bottom - inset)
		}
	}
	const filters = control.closest<HTMLElement>(
		'.filter-container, #filter-container',
	)
	const filterStyle = filters ? getComputedStyle(filters) : null
	const spansColumns =
		filterStyle?.display === 'grid' &&
		filterStyle.gridTemplateColumns.split(' ').length > 1
	const gridWidth =
		filters && filterStyle && spansColumns
			? filters.clientWidth -
				Number.parseFloat(filterStyle.paddingLeft) -
				Number.parseFloat(filterStyle.paddingRight)
			: 0
	const preferredLeft =
		filters && filterStyle && spansColumns
			? filters.getBoundingClientRect().left +
				filters.clientLeft +
				Number.parseFloat(filterStyle.paddingLeft)
			: anchor.left
	menu.style.left = '0px'
	menu.style.right = 'auto'
	menu.style.top = '0px'
	menu.style.bottom = 'auto'
	menu.style.maxHeight = ''
	menu.style.maxWidth = `${Math.max(0, right - left)}px`
	menu.style.minWidth = `${Math.min(Math.max(control.clientWidth, gridWidth), Math.max(0, right - left))}px`
	const rect = menu.getBoundingClientRect()
	const gap =
		Number.parseFloat(getComputedStyle(document.documentElement).fontSize) *
		0.25
	const availableHeight = Math.max(0, bottom - top)
	const below = Math.min(
		availableHeight,
		Math.max(0, bottom - anchor.bottom - gap),
	)
	const above = Math.min(availableHeight, Math.max(0, anchor.top - gap - top))
	const openAbove = rect.height > below && above > below
	const height = Math.min(rect.height, openAbove ? above : below)
	const x = Math.max(left, Math.min(preferredLeft, right - rect.width))
	const y = Math.max(
		top,
		Math.min(
			openAbove ? anchor.top - gap - height : anchor.bottom + gap,
			bottom - height,
		),
	)
	menu.style.left = `${x - rect.left}px`
	menu.style.top = `${y - rect.top}px`
	menu.style.maxHeight = `${height}px`
}

function getSelectedValues(control: HTMLElement): string[] {
	return getValues(
		control,
		'.multi-select-option:not([data-all-option="true"]):is([aria-selected="true"], [aria-checked="true"])',
	)
}

function getOptionValues(control: HTMLElement): string[] {
	return getValues(
		control,
		'.multi-select-option:not([data-all-option="true"])',
	)
}

function getAllOptionValues(control: HTMLElement): string[] {
	return getValues(
		control,
		'.multi-select-option:not([data-all-option="true"]):not([data-included-in-all="false"])',
	)
}

function getValues(control: HTMLElement, selector: string): string[] {
	return Array.from(control.querySelectorAll<HTMLButtonElement>(selector))
		.map((option) => option.dataset.value ?? '')
		.filter(Boolean)
}

/** Search only changes visible options, never the selected filter values. */
export function filterMultiSelectOptions(control: HTMLElement): void {
	const input = control.querySelector<HTMLInputElement>(
		'.multi-select-search',
	)
	if (!input) return
	const query = input.value.trim().toLocaleLowerCase()
	let matches = 0
	control
		.querySelectorAll<HTMLElement>('.multi-select-option')
		.forEach((option) => {
			const isReset = option.dataset.allOption === 'true'
			option.hidden =
				!isReset &&
				!(option.dataset.label ?? option.dataset.value ?? '')
					.toLocaleLowerCase()
					.includes(query)
			if (!isReset && !option.hidden) matches++
		})
	const empty = control.querySelector<HTMLElement>('.multi-select-empty')
	if (empty) empty.hidden = matches > 0
	positionMultiSelect(control)
}

export function handleMultiSelectNavigation(event: KeyboardEvent): void {
	const target = event.target
	if (!(target instanceof HTMLElement)) return
	const control = target.closest<HTMLElement>('.tt-multi-select')
	if (!control || !['ArrowDown', 'ArrowUp'].includes(event.key)) return
	const trigger = control.querySelector<HTMLButtonElement>(
		'.multi-select-trigger',
	)
	if (trigger?.getAttribute('aria-expanded') !== 'true') {
		closeMultiSelects(control.parentElement ?? document, control)
		setMultiSelectOpen(control, true)
		const first =
			control.querySelector<HTMLElement>('.multi-select-search') ??
			control.querySelector<HTMLElement>(
				'.multi-select-option:not([hidden])',
			)
		first?.focus()
	} else {
		const options = Array.from(
			control.querySelectorAll<HTMLButtonElement>('.multi-select-option'),
		).filter((option) => !option.hidden)
		const index = options.indexOf(target as HTMLButtonElement)
		const next = index + (event.key === 'ArrowDown' ? 1 : -1)
		if (index === -1)
			(event.key === 'ArrowDown' ? options[0] : options.at(-1))?.focus()
		else if (next < 0)
			(
				control.querySelector<HTMLInputElement>(
					'.multi-select-search',
				) ?? trigger
			)?.focus()
		else options[Math.min(next, options.length - 1)]?.focus()
	}
	event.preventDefault()
}

/** Replaces options while retaining the open menu, search, focus and scroll. */
export function replaceMultiSelect(
	container: HTMLElement,
	control: HTMLElement,
	markup: string,
): void {
	const selectId = control.dataset.selectId
	const wasOpen =
		control
			.querySelector<HTMLButtonElement>('.multi-select-trigger')
			?.getAttribute('aria-expanded') === 'true'

	const search = control.querySelector<HTMLInputElement>(
		'.multi-select-search',
	)
	const query = search?.value ?? ''
	const active = document.activeElement as HTMLElement | null
	const hadFocus = !!active && control.contains(active)
	const focusedValue = active?.closest<HTMLElement>('.multi-select-option')
		?.dataset.value
	const scrollTop =
		control.querySelector('.multi-select-options')?.scrollTop ?? 0
	control.outerHTML = markup
	if (!wasOpen || !selectId) return

	const replacement = Array.from(
		container.querySelectorAll<HTMLElement>('.tt-multi-select'),
	).find((item) => item.dataset.selectId === selectId)
	if (replacement) {
		setMultiSelectOpen(replacement, true)
		const input = replacement.querySelector<HTMLInputElement>(
			'.multi-select-search',
		)
		if (input) input.value = query
		filterMultiSelectOptions(replacement)
		if (hadFocus) {
			const option = Array.from(
				replacement.querySelectorAll<HTMLButtonElement>(
					'.multi-select-option',
				),
			).find((item) => item.dataset.value === focusedValue)
			if (focusedValue !== undefined && option && !option.hidden)
				option.focus()
			else input?.focus()
		}
		const options = replacement.querySelector('.multi-select-options')
		if (options) options.scrollTop = scrollTop
	}
}
